import { useEffect, useRef, useState } from "react";
import { MapPin, Maximize2, MessageCircle, Minimize2, Phone, User, X } from "lucide-react";
import { telHref, waHref, resolveTrackingEta } from "@/lib/liveTrackingMap";
import { useLiveDeliveryMap } from "@/hooks/useLiveDeliveryMap";

export default function CustomerLiveTracking({ tracking }) {
  const mapEl = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);

  const { routeEtaMinutes, hasDriverPosition, driverName } = useLiveDeliveryMap({
    mapEl,
    tracking,
    fullscreen,
    lockDestination: true,
    variant: "customer",
  });

  const showMap = Boolean(tracking?.show_map);
  const eta = resolveTrackingEta(tracking, routeEtaMinutes, { variant: "customer" });
  const driverTel = telHref(tracking?.driver_phone_tel);
  const driverWa = waHref(tracking?.driver_phone || tracking?.driver_phone_tel);
  const destLat = tracking?.destination_lat;
  const destAddress = tracking?.destination_address;

  useEffect(() => {
    if (!fullscreen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  if (!showMap) return null;

  const etaTime = eta.ready ? (
    <>
      {eta.minutes} <span className="customer-live-track-eta-big-unit">min</span>
    </>
  ) : (
    <span className="customer-live-track-eta-buffer">{eta.bufferMessage}</span>
  );

  const statusSheet = (
    <div className="customer-live-track-sheet">
      <div className="customer-live-track-eta-block">
        <p className="customer-live-track-eta-kicker">Arriving in</p>
        <p
          className={`customer-live-track-eta-big${eta.ready ? "" : " customer-live-track-eta-big--buffer"}`}
          aria-label={
            eta.ready
              ? `Arriving in ${eta.minutes} minutes`
              : eta.bufferMessage || "Arrival time pending"
          }
        >
          {etaTime}
        </p>
        <p className="customer-live-track-eta-status">
          {eta.ready
            ? "Your cake is on the way"
            : eta.bufferMessage || "Buffering — live ETA when driver GPS is synced"}
        </p>
      </div>

      <div className="customer-live-track-sheet-divider" aria-hidden />

      <div className="customer-live-track-driver-row">
        <div className="customer-live-track-driver-avatar" aria-hidden>
          <User size={22} strokeWidth={2.25} />
        </div>
        <div className="customer-live-track-driver-meta">
          <p className="customer-live-track-driver-name">{driverName}</p>
          <p className="customer-live-track-driver-role">Delivery Partner</p>
        </div>
        <div className="customer-live-track-driver-actions">
          {driverWa ? (
            <a
              href={driverWa}
              className="customer-live-track-icon-btn customer-live-track-icon-btn--chat"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp driver"
            >
              <MessageCircle size={20} strokeWidth={2.25} />
            </a>
          ) : null}
          {driverTel ? (
            <a href={driverTel} className="customer-live-track-icon-btn" aria-label="Call driver">
              <Phone size={20} strokeWidth={2.25} />
            </a>
          ) : null}
        </div>
      </div>

      {destAddress ? (
        <>
          <div className="customer-live-track-sheet-divider" aria-hidden />
          <div className="customer-live-track-address-row">
            <MapPin size={18} strokeWidth={2.25} className="customer-live-track-address-icon" aria-hidden />
            <p className="customer-live-track-address-text">
              <span className="customer-live-track-address-label">Delivering to</span>
              {destAddress}
            </p>
          </div>
        </>
      ) : null}
    </div>
  );

  return (
    <section
      className={`customer-live-track${fullscreen ? " customer-live-track--fullscreen" : ""}`}
      aria-label="Live delivery tracking"
    >
      {fullscreen ? (
        <div className="customer-live-track-fullscreen-bar">
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="customer-live-track-fs-close-btn"
            aria-label="Close full screen map"
          >
            <X size={20} />
          </button>
          <h2 className="customer-live-track-title customer-live-track-title--bar">
            Track your delivery, <span className="customer-live-track-live">Live</span>
          </h2>
          <div className="customer-live-track-fs-spacer" aria-hidden />
        </div>
      ) : (
        <h2 className="customer-live-track-title">
          Track your delivery, <span className="customer-live-track-live">Live</span>
        </h2>
      )}

      <div className="customer-live-track-map-shell">
        <div
          ref={mapEl}
          className={`customer-live-track-map${fullscreen ? " customer-live-track-map--fullscreen" : ""}`}
          role="img"
          aria-label="Delivery map"
        />
        {!destLat ? (
          <div className="customer-live-track-map-fallback">
            {tracking?.maps_directions_url ? (
              <a
                href={tracking.maps_directions_url}
                target="_blank"
                rel="noopener noreferrer"
                className="customer-live-track-map-fallback-link"
              >
                Open delivery route in Google Maps
              </a>
            ) : (
              "Loading map…"
            )}
          </div>
        ) : null}
        <button
          type="button"
          className="customer-live-track-expand-btn"
          onClick={() => setFullscreen((v) => !v)}
          aria-label={fullscreen ? "Exit full screen map" : "Open full screen map"}
        >
          {fullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
        </button>

        {fullscreen ? (
          <div className="customer-live-track-fullscreen-overlay">{statusSheet}</div>
        ) : null}
      </div>

      {!fullscreen ? statusSheet : null}

      {!fullscreen ? (
        <p className="customer-live-track-foot">No guessing. No follow-ups.</p>
      ) : null}
    </section>
  );
}
